import { inject } from '@angular/core';
import { MetaDefinition } from '@angular/platform-browser';
import { ResolveFn } from '@angular/router';
import { GalleryDto, TenantDto } from '@nusign/picflow-backend-types';
import { catchError, from, map, of } from 'rxjs';
import { calcLegacyTheme } from '../../gallery/lib/calc-legacy-theme';
import { calcResizedUrlFromUuid } from '../../lib/calc-resized-url';
import { getFullPath } from '../../lib/get-full-path';
import { getMetaDescriptionTags, getMetaImageTags, getMetaTitleTags } from '../../lib/meta-helpers';
import { PICFLOW_CONFIG } from '../../picflow-config';
import { GalleryService } from '../../services/gallery.service';

export const getTitle = (gallery: GalleryDto, tenant: TenantDto) => {
  const tenantName = tenant?.name;
  if (tenantName && gallery.title) return `${gallery.title} by ${tenantName}`;
  if (tenantName && !gallery.title) return `Gallery by ${tenantName}`;
  if (!tenantName && gallery.title) return `${gallery.title}`;
  return 'Picflow Gallery';
};

const getDescription = (gallery: GalleryDto, tenant: TenantDto) => {
  const tenantName = tenant?.name;
  if (!tenantName) return;
  return `Photo Gallery by ${tenantName}`;
};

const getThemeColor = (gallery: GalleryDto, isAdvancedMode: boolean) => {
  if (isAdvancedMode) return '#111319'; // Advanced mode uses dark theme by default
  const theme = gallery.theme || calcLegacyTheme(gallery);
  return theme.layout_page.background_color;
};

export const publicGalleryPageMetaResolver: ResolveFn<{ title: string; tags: MetaDefinition[] }> = route => {
  const galleryService = inject(GalleryService);
  const config = inject(PICFLOW_CONFIG);
  const path = getFullPath(route);
  const isAdvancedMode = path[path.length - 1] === 'advanced';

  return from(galleryService.getPublicGalleryFromSnapshot(route)).pipe(
    map(gallery => {
      if (!gallery) return;
      // const noIndex = !gallery.show_in_profile;
      const tenant = gallery.tenant as TenantDto;

      return {
        title: getTitle(gallery, tenant),
        tags: [
          ...getMetaTitleTags(getTitle(gallery, tenant)),
          ...getMetaDescriptionTags(getDescription(gallery, tenant)),
          ...getMetaImageTags(calcResizedUrlFromUuid(config.assetsEndpoint, gallery.teaser.uuid, `1200x630`)),
          { name: 'theme-color', content: getThemeColor(gallery, isAdvancedMode) },
          // noIndex ? noIndexMetaTag : undefined,
        ].filter(Boolean),
      };
    }),
    catchError(() => of(undefined))
  );
};
